import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateProjectName } from "../../../store/actions/frontApp.Action";
import FormSelect from "../../forms-component/globalSelect";
import { getCombinedProjects } from "./helper";
import toast from "react-hot-toast";

interface ProjectNameSelectProps {
    originalRow: any;
    colorMode?: string;
}

export const ProjectNameSelect: React.FC<ProjectNameSelectProps> = ({ originalRow }) => {
    const dispatch = useDispatch();

    const existingProjectName = typeof originalRow?.projectId === 'object'
        ? originalRow?.projectId?.name
        : (originalRow?.projectName || "");

    const [selectedProject, setSelectedProject] = useState<string>(
        typeof originalRow?.projectId === 'object' ? originalRow?.projectId?._id : (originalRow?.projectId || "")
    );

    React.useEffect(() => {
        const currentId = typeof originalRow?.projectId === 'object'
            ? originalRow?.projectId?._id
            : (originalRow?.projectId || "");
        setSelectedProject(currentId);
    }, [originalRow?.projectId, originalRow?.comment_id]);

    const { dataForTable } = useSelector((state: any) => state.masterData);
    const projectsList = useMemo(() => getCombinedProjects(dataForTable), [dataForTable]);

    const selectedProjectData = projectsList.find((p: any) => {
        const id = typeof p === 'object' ? p?._id : p;
        return id === selectedProject;
    });
    const displayProjectName = selectedProjectData
        ? (typeof selectedProjectData === 'object' ? (selectedProjectData.name || selectedProjectData.projectName) : selectedProjectData)
        : existingProjectName;

    const handleChange = (selectedOption: any) => {
        if (originalRow?.timer?.isRunning) {
            toast.error("Please pause the timer");
            return;
        }
        const newValue = selectedOption ? selectedOption.value : "";
        setSelectedProject(newValue);
        const payload = { projectId: newValue };
        dispatch(updateProjectName(payload, originalRow?.comment_id));
    };

    const projectOptions = [
        { label: "Blank", value: "" },
        ...projectsList.map((proj: any) => ({
            value: typeof proj === 'object' ? proj?._id : proj,
            label: typeof proj === 'object' ? (proj?.name || proj?.projectName || '') : proj
        }))
    ];

    return (
        <div style={{ minWidth: '160px' }} title={displayProjectName}>
            <FormSelect
                value={selectedProject}
                options={projectOptions}
                onChange={handleChange}
                placeholder="Select Project"
                classNames={{
                    option: (state: any) => "custom-dropdown-option",
                    control: (state: any) => "custom-dropdown-single-control",
                    menu: (state: any) => "custom-dropdown-menu"
                }}
            />
        </div>
    );
};

// ─── Manager Select ───────────────────────────────────────────────────────────

interface ManagerSelectProps {
    originalRow: any;
    colorMode?: string;
}

export const ManagerSelect: React.FC<ManagerSelectProps> = ({ originalRow }) => {
    const dispatch = useDispatch();

    const getManagerLabel = (u: any) =>
        typeof u === 'object'
            ? `${u?.FirstName || ''} ${u?.LastName || ''}`.trim() || u?.username || ''
            : u;

    // taskManagerId comes as an array from the API — take the first item
    const firstManager = Array.isArray(originalRow?.manager)
        ? originalRow.manager[0]
        : (originalRow?.manager ?? null);

    const existingManagerName = firstManager ? getManagerLabel(firstManager) : "";

    const getManagerId = (m: { _id?: string } | string | null): string => typeof m === 'object' && m !== null ? (m._id || "") : (m || "");

    const [selectedManager, setSelectedManager] = useState<string>(
        getManagerId(firstManager)
    );

    React.useEffect(() => {
        const currentId = getManagerId(firstManager);
        setSelectedManager(currentId);
    }, [firstManager, originalRow?.comment_id]);

    const { dataForTable } = useSelector((state: any) => state.masterData);
    const usersList = dataForTable?.data?.allTaskManagers || [];

    const selectedManagerData = usersList.find((u: any) => {
        const id = typeof u === 'object' ? u?._id : u;
        return id === selectedManager;
    });

    const displayManagerName = selectedManagerData
        ? getManagerLabel(selectedManagerData)
        : "";

    const handleChange = (selectedOption: any) => {
        if (originalRow?.timer?.isRunning) {
            toast.error("Please pause the timer");
            return;
        }
        const newValue = selectedOption ? selectedOption.value : "";
        setSelectedManager(newValue);
        const payload = { manager: newValue };
        dispatch(updateProjectName(payload, originalRow?.comment_id));
    };

    const managerOptions = [
        { label: "Blank", value: "" },
        ...usersList.map((user: any) => ({
            value: typeof user === 'object' ? user?._id : user,
            label: getManagerLabel(user)
        }))
    ];

    return (
        <div style={{ minWidth: '160px' }} title={displayManagerName}>
            <FormSelect
                value={selectedManager}
                options={managerOptions}
                onChange={handleChange}
                placeholder="Select Manager"
                classNames={{
                    option: (state: any) => "custom-dropdown-option",
                    control: (state: any) => "custom-dropdown-single-control",
                    menu: (state: any) => "custom-dropdown-menu"
                }}
            />
        </div>
    );
};

export default ProjectNameSelect;
